import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, RefreshCcw, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/use-toast';
import AlertBox from '@/components/AlertBox';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldSearch from '@/components/FormFieldSearch';
import Loader from '@/components/Loader';
import {
  useCaseTypeCourtMappings,
  useCaseTypes,
  useCourtSearchOptions,
  useCourtTypes,
  useCreateCaseTypeCourtMapping,
  useDeleteCaseTypeCourtMapping,
} from '@/services/caseTypeCourtMappingService';
import type { CaseTypeCourtMapping as Mapping, CourtOption } from '@/types/caseTypeCourtMapping.types';
import { displayError } from '@/lib/error';
import { caseTypeCourtMappingSchema, CaseTypeCourtMappingFormValues } from './caseTypeCourtMapping.schema';

const CaseTypeCourtMapping: FC = () => {
  const form = useForm<CaseTypeCourtMappingFormValues>({
    resolver: zodResolver(caseTypeCourtMappingSchema),
    defaultValues: { caseTypeId: '', courtId: '', courtType: null },
  });
  const { watch, setValue, handleSubmit } = form;
  const caseTypeId = watch('caseTypeId');
  const courtId = watch('courtId');
  const courtType = watch('courtType');

  // Court picked from the Court Name list. While typing, `courtId` holds the search text, so a court only
  // counts once it has been picked and the field still holds its id.
  const [selectedCourt, setSelectedCourt] = useState<CourtOption | null>(null);
  // Bumped on reset to remount both court pickers; the Select keeps showing its last value when cleared.
  const [courtFieldsKey, setCourtFieldsKey] = useState(0);
  const [mappingToRemove, setMappingToRemove] = useState<Mapping | null>(null);

  const caseTypesQuery = useCaseTypes();
  const courtTypesQuery = useCourtTypes();
  const mappingsQuery = useCaseTypeCourtMappings(caseTypeId || undefined);
  const createMutation = useCreateCaseTypeCourtMapping();
  const deleteMutation = useDeleteCaseTypeCourtMapping();

  const caseTypeOptions = useMemo(
    () => (caseTypesQuery.data?.caseTypes ?? []).map((item) => ({ label: item.name, value: item.caseTypeId })),
    [caseTypesQuery.data]
  );

  const pickedCourt = selectedCourt && courtId === selectedCourt.courtId ? selectedCourt : null;
  const courtTypeForSelectedCourt = pickedCourt?.type ?? null;
  const courtTypeOptions = useMemo(() => {
    const types = courtTypeForSelectedCourt ? [courtTypeForSelectedCourt] : (courtTypesQuery.data?.types ?? []);
    return types.map((type) => ({ label: type, value: type }));
  }, [courtTypeForSelectedCourt, courtTypesQuery.data]);

  const mappings = mappingsQuery.data?.mappings ?? [];
  const canAdd = !!caseTypeId && (!!pickedCourt || !!courtType) && !createMutation.isPending;

  const resetCourtFields = () => {
    setValue('courtId', '');
    setValue('courtType', null);
    setSelectedCourt(null);
    setCourtFieldsKey((key) => key + 1);
  };

  const onAdd = handleSubmit((values) => {
    if (!canAdd) return;
    createMutation.mutate(
      {
        caseTypeId: values.caseTypeId,
        courtId: pickedCourt ? pickedCourt.courtId : null,
        courtType: values.courtType ? values.courtType : null,
      },
      {
        onSuccess: (response) => {
          toast({ description: response.message, variant: 'default' });
          resetCourtFields();
        },
        onError: (error) => displayError(error),
      }
    );
  });

  const onConfirmRemove = () => {
    if (!mappingToRemove) return;
    deleteMutation.mutate(mappingToRemove.caseTypeCourtMappingId, {
      onSuccess: (response) => toast({ description: response.message, variant: 'default' }),
      onError: (error) => displayError(error),
      onSettled: () => setMappingToRemove(null),
    });
  };

  const renderMappings = () => {
    if (!caseTypeId) {
      return (
        <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed bg-white p-8 text-center text-sm text-placeholder">
          Select a Case Type to view and manage its courts.
        </div>
      );
    }

    if (mappingsQuery.isError) {
      return (
        <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed bg-white p-8 text-center">
          <div className="flex max-w-md flex-col items-center gap-4">
            <AlertTriangle className="h-10 w-10 text-error" />
            <p className="text-sm text-placeholder">Unable to load the court mappings for this case type.</p>
            <Button type="button" variant="outline" onClick={() => mappingsQuery.refetch()}>
              <RefreshCcw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </div>
        </div>
      );
    }

    return (
      <Loader isLoading={mappingsQuery.isLoading} className="min-h-[200px]">
        {mappings.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed bg-white p-8 text-center text-sm text-placeholder">
            No courts are mapped to this case type.
          </div>
        ) : (
          <div className="rounded-lg border bg-white">
            {/* Fixed layout so the columns don't shift with the court names in the list. */}
            <Table className="[&>table]:table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/2">Court Name</TableHead>
                  <TableHead>Court Type</TableHead>
                  <TableHead className="w-[100px]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow key={mapping.caseTypeCourtMappingId}>
                    <TableCell className="break-words">{mapping.court?.name ?? '—'}</TableCell>
                    <TableCell className="break-words">{mapping.courtType ?? '—'}</TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove ${mapping.court?.name ?? mapping.courtType ?? 'mapping'}`}
                        onClick={() => setMappingToRemove(mapping)}
                      >
                        <Trash2 className="h-4 w-4 text-error" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Loader>
    );
  };

  return (
    <div className="flex w-full flex-col gap-5">
      <h1 className="text-3xl font-semibold text-black">Case Type–Court Mapping</h1>

      <Form {...form}>
        <form onSubmit={onAdd} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormFieldSelect
              name="caseTypeId"
              label="Case Type"
              placeholder="Select Case Type"
              options={caseTypeOptions}
              isLoading={caseTypesQuery.isLoading}
              required
              onChange={resetCourtFields}
            />
          </div>

          <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-[1fr_1fr_auto]">
            <FormFieldSearch
              key={`court-name-${courtFieldsKey}`}
              name="courtId"
              label="Court Name"
              placeholder="Search Court"
              props={{ Icon: Search }}
              disabled={!caseTypeId}
              api={useCourtSearchOptions}
              onChange={(_value, option) => {
                const court = option as CourtOption | undefined;
                setSelectedCourt(court ?? null);
                setValue('courtType', court?.type ?? null);
              }}
            />
            <FormFieldSelect
              key={`court-type-${courtFieldsKey}`}
              name="courtType"
              label="Court Type"
              placeholder="Select Court Type"
              options={courtTypeOptions}
              isLoading={courtTypesQuery.isLoading}
              disabled={!caseTypeId}
              clearable
            />
            <Button type="submit" disabled={!canAdd}>
              {createMutation.isPending ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </form>
      </Form>

      {renderMappings()}

      <AlertBox
        open={!!mappingToRemove}
        onOpenChange={(open) => !open && setMappingToRemove(null)}
        title="Remove Mapping"
        description={`Remove ${[mappingToRemove?.court?.name, mappingToRemove?.courtType].filter(Boolean).join(' – ')} from ${mappingToRemove?.caseType.name ?? 'this case type'}? The case type and court are not changed.`}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Remove"
        confirmBtnClick={onConfirmRemove}
        isLoading={deleteMutation.isPending}
        warning
      />
    </div>
  );
};

export default CaseTypeCourtMapping;
